import { capitalize, formatToDollar, formatToPercent, formatYesNo } from "@inrev/common";
import { useAdminGetCarrierPreviews } from "@portal/api/account/admin";
import {
	createSummaryTo,
	SummaryCardSection,
	SummaryField,
	SummaryFieldGroup,
	SummarySection,
} from "@portal/components/multiform";
import { useFormContext } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { formatEmpty } from "../../../../../../utils";
import { type AdminCombinedFormData, fp } from "../../schema";

export const AccountTermsSummary = () => {
	const { getValues } = useFormContext<AdminCombinedFormData>();
	const { pathname } = useLocation();
	const { allCarriers } = useAdminGetCarrierPreviews();
	const formValues = getValues();
	const terms = formValues.accountTerms;
	const indemnityAgreement = formValues.indemnityAgreement;
	const to = createSummaryTo(pathname, "accountTerms");

	return (
		<SummaryCardSection header="Account Terms" to={{ pathname, section: "accountTerms" }}>
			<SummaryFieldGroup>
				<SummaryField
					label="Status"
					value={formatEmpty(terms?.status ? capitalize(terms.status) : undefined)}
					fieldPath={fp("accountTerms.status")}
					to={to("accountTerms.status")}
				/>
			</SummaryFieldGroup>
			{terms?.status === "approved" && (
				<>
					<SummarySection header="InRev Bonding Line" to={to("bonding-line")}>
						<SummaryFieldGroup>
							<SummaryField
								label="Rate"
								value={formatEmpty(formatToPercent(terms.bondingLine?.rate))}
								fieldPath={fp("accountTerms.bondingLine.rate")}
								to={to("accountTerms.bondingLine.rate")}
							/>
							<SummaryField
								label="Single Limit"
								value={formatEmpty(formatToDollar(terms.bondingLine?.singleLimit))}
								fieldPath={fp("accountTerms.bondingLine.singleLimit")}
								to={to("accountTerms.bondingLine.singleLimit")}
							/>
							<SummaryField
								label="Aggregate Limit"
								value={formatEmpty(formatToDollar(terms.bondingLine?.aggregateLimit))}
								fieldPath={fp("accountTerms.bondingLine.aggregateLimit")}
								to={to("accountTerms.bondingLine.aggregateLimit")}
							/>
							<SummaryField
								label="Publish"
								value={formatEmpty(formatYesNo(terms.bondingLine?.isPublished))}
								fieldPath={fp("accountTerms.bondingLine.isPublished")}
								to={to("accountTerms.bondingLine.isPublished")}
							/>
						</SummaryFieldGroup>
					</SummarySection>
					{allCarriers &&
						terms.carrierBondingLines?.length > 0 &&
						allCarriers.map((carrier, idx) => (
							<SummarySection
								key={carrier.name}
								header={`${carrier.displayName} Bonding Line`}
								to={to(`bonding-line-${carrier.name}`)}
							>
								<SummaryFieldGroup>
									<SummaryField
										label="Rate"
										value={formatEmpty(
											formatToPercent(terms.carrierBondingLines[idx]?.bondingLine?.rate),
										)}
										fieldPath={`accountTerms.carrierBondingLines.${idx}.bondingLine.rate`}
										to={to("accountTerms")}
									/>
									<SummaryField
										label="Single Limit"
										value={formatEmpty(
											formatToDollar(terms.carrierBondingLines[idx]?.bondingLine?.singleLimit),
										)}
										fieldPath={`accountTerms.carrierBondingLines.${idx}.bondingLine.singleLimit`}
										to={to("accountTerms")}
									/>
									<SummaryField
										label="Aggregate Limit"
										value={formatEmpty(
											formatToDollar(terms.carrierBondingLines[idx]?.bondingLine?.aggregateLimit),
										)}
										fieldPath={`accountTerms.carrierBondingLines.${idx}.bondingLine.aggregateLimit`}
										to={to("accountTerms")}
									/>
								</SummaryFieldGroup>
							</SummarySection>
						))}
				</>
			)}
			<SummaryFieldGroup>
				<SummaryField
					label="Indemnity Agreement"
					value={formatEmpty(indemnityAgreement?.name && `${indemnityAgreement.name}.pdf`)}
					fieldPath={fp("indemnityAgreement")}
					to={to("indemnityAgreement")}
				/>
			</SummaryFieldGroup>
		</SummaryCardSection>
	);
};
